import { useState, useEffect, useRef } from "react";
import Modal from "../../common/components/Modal";
import BasicButton from "../../common/components/BasicButton";
import Editor from "../../common/components/Editor";
import { OriginalPost } from "./OriginalPostTable";

interface AddOriginalPostModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (post: Omit<OriginalPost, "id" | "createdAt" | "updatedAt">) => Promise<void>;
  isLoading?: boolean;
}

export default function AddOriginalPostModal({
  open,
  onClose,
  onSave,
  isLoading = false,
}: AddOriginalPostModalProps) {
  const [formData, setFormData] = useState({
    title: "",
    status: "NEW" as OriginalPost["status"],
    tags: [] as string[],
    memo: "",
    content: "",
    sourceUrl: "",
  });
  const [tagInput, setTagInput] = useState("");
  const titleInputRef = useRef<HTMLInputElement>(null);

  // 모달이 열릴 때 제목 입력란에 포커스
  useEffect(() => {
    if (open && titleInputRef.current) {
      // 약간의 딜레이를 두어 모달 애니메이션 후 포커스
      setTimeout(() => {
        titleInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.sourceUrl.trim() || !formData.content.trim()) {
      alert("제목, 원문출처, 내용은 필수항목입니다.");
      return;
    }

    try {
      await onSave({
        title: formData.title.trim(),
        status: formData.status,
        tags: formData.tags,
        memo: formData.memo,
        content: formData.content,
        sourceUrl: formData.sourceUrl,
      });

      // 저장 성공 시에만 폼 초기화 및 모달 닫기
      setFormData({
        title: "",
        status: "NEW",
        tags: [],
        memo: "",
        content: "",
        sourceUrl: "",
      });
      setTagInput("");
    } catch (error) {
      // 에러 발생 시 모달을 닫지 않고 사용자가 수정할 수 있도록 함
      console.error('Failed to save:', error);
    }
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleTagInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  if (!open) return null;

  return (
    <Modal 
      open={open} 
      onClose={onClose}
      title="원문 추가하기"
      width="70%"
      height="90%"
    >
      <div className="flex h-full flex-col">
        <div className="flex-1 overflow-y-auto px-6 py-6 pb-8">
          <div className="space-y-6">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              제목 *
            </label>
            <input
              ref={titleInputRef}
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
              placeholder="원문 제목을 입력하세요"
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              원문출처 *
            </label>
            <input
              type="url"
              value={formData.sourceUrl}
              onChange={(e) => setFormData(prev => ({ ...prev, sourceUrl: e.target.value }))}
              className="w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
              placeholder="https://example.com"
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              태그
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleTagInputKeyPress}
                className="flex-1 rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
                placeholder="태그를 입력하고 Enter 또는 추가 버튼을 누르세요"
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="rounded bg-gray-500 px-4 py-2 text-sm text-white hover:bg-gray-600"
              >
                추가
              </button>
            </div>
            
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center rounded bg-blue-100 px-3 py-1 text-sm text-blue-800"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              메모
            </label>
            <textarea
              value={formData.memo}
              onChange={(e) => setFormData(prev => ({ ...prev, memo: e.target.value }))}
              rows={3}
              className="w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
              placeholder="내부 메모나 참고사항을 입력하세요"
            />
          </div>

          <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                내용 *
              </label>
              <div className="mb-8">
                <div 
                  style={{ 
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    height: '370px',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <div style={{ flex: 1, minHeight: 0 }}>
                    <Editor 
                      value={formData.content}
                      onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
                      height="100%"
                      mediaFolder="original-post"
                    />
                  </div>
                </div>
              </div>
            </div>
            {/* 에디터 하단 여백 */}
            <div className="h-6"></div>
          </div>
        </div>
        
        {/* 하단 고정 버튼 */}
        <div className="sticky bottom-0 flex justify-end gap-3 border-t bg-white p-4 shadow-lg">
          <BasicButton
            text="취소"
            variant="outlined"
            onClick={onClose}
            type="button"
          />
          <BasicButton
            text={isLoading ? "저장 중..." : "저장"}
            variant="primary"
            onClick={handleSubmit}
            disabled={isLoading}
          />
        </div>
      </div>
    </Modal>
  );
}